import React, { useState } from "react";
import { PlateElement, type PlateElementProps, useEditorRef } from "platejs/react";
import type { TElement } from "platejs";
import { toast } from "sonner";
import { authFetch } from "../../../../../services/authFetch";
import { BlockRowContext } from "../../LogCard";
import { BLOCK_PLECA, BLOCK_LOG, BLOCK_OTF, BLOCK_TRANSCRIPT, nodeText } from "../schema";

const SOURCE_TYPES = new Set<string>([BLOCK_LOG, BLOCK_OTF, BLOCK_TRANSCRIPT]);

/**
 * Recolecta el texto de los logs/transcripciones que viven ARRIBA de la pleca
 * (hacia el pasado), acotado al segmento: paramos al toparnos con otra pleca,
 * porque esos logs ya pertenecen a la pleca anterior. Devuelve el texto en
 * orden cronológico (de arriba hacia la pleca).
 */
function collectContextAbove(doc: TElement[], plecaIndex: number): string {
  const lines: string[] = [];
  for (let i = plecaIndex - 1; i >= 0; i--) {
    const node = doc[i] as TElement;
    if ((node as any)?.type === BLOCK_PLECA) break;
    if (SOURCE_TYPES.has((node as any)?.type)) {
      const text = nodeText(node);
      if (text) lines.push(text);
    }
  }
  return lines.reverse().join("\n");
}

/**
 * PLECA — a lower-third / on-screen caption strip. Editable text styled as the
 * graphic that will be burned into the VTR.
 */
export function PlecaBlock(props: PlateElementProps) {
  const editor = useEditorRef();
  const blockRowCtx = React.useContext(BlockRowContext);
  const [loading, setLoading] = useState(false);

  const handleGenerate = async () => {
    if (loading) return;
    const path = editor.api.findPath(props.element);
    if (!path) return;
    const doc = editor.children as TElement[];
    const context = collectContextAbove(doc, path[0]);
    if (!context.trim()) {
      toast.warning("No hay logs ni transcripciones arriba para generar la pleca");
      return;
    }

    setLoading(true);
    try {
      const res = await authFetch("/api/assembly/pleca/generate", {
        method: "POST",
        body: JSON.stringify({ context }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || `HTTP ${res.status}`);
      }
      const { pleca } = (await res.json()) as { pleca?: string };
      const text = (pleca || "").trim();
      if (!text) throw new Error("La IA no devolvió una pleca");

      // Recompute path: el doc pudo cambiar durante el await.
      const at = editor.api.findPath(props.element);
      if (!at) return;
      editor.tf.removeNodes({ at });
      editor.tf.insertNodes({ type: BLOCK_PLECA, children: [{ text }] } as TElement, {
        at,
        select: true,
      });
    } catch (e: any) {
      toast.error(`No se pudo generar la pleca: ${e.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <PlateElement {...props} style={{ margin: "8px 0" }}>
      <div
        style={{
          borderLeft: "3px solid #f59e0b",
          background: "rgba(245,158,11,0.10)",
          borderRadius: 4,
          padding: "8px 12px",
        }}
      >
        <div
          contentEditable={false}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: 4,
          }}
        >
          <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
            {blockRowCtx?.handleRef && (
              <span
                ref={blockRowCtx.handleRef as any}
                title="Arrastrar"
                style={{ display: "inline-flex", cursor: "grab", color: "#b47410" }}
              >
                <i className="bx bx-grid-vertical" style={{ fontSize: "1rem" }} />
              </span>
            )}
            <span
              style={{
                fontSize: "0.6rem",
                fontWeight: 800,
                letterSpacing: 1.5,
                color: "#f59e0b",
              }}
            >
              PLECA
            </span>
          </span>
          <button
            type="button"
            title="Generar la pleca a partir de los logs/transcripciones de arriba"
            onMouseDown={(e) => e.preventDefault()}
            onClick={handleGenerate}
            disabled={loading}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 4,
              background: "rgba(245,158,11,0.15)",
              color: "#f59e0b",
              border: "1px solid rgba(245,158,11,0.4)",
              borderRadius: 4,
              padding: "2px 8px",
              fontSize: "0.6rem",
              fontWeight: 700,
              letterSpacing: 1,
              cursor: loading ? "default" : "pointer",
              opacity: loading ? 0.6 : 1,
            }}
          >
            <i className={loading ? "bx bx-loader-alt bx-spin" : "bx bx-magic-wand"} />
            {loading ? "GENERANDO…" : "GENERAR"}
          </button>
        </div>
        <div
          style={{
            color: "#fff",
            fontWeight: 700,
            fontSize: "0.95rem",
            textTransform: "uppercase",
            lineHeight: 1.3,
            outline: "none",
          }}
        >
          {props.children}
        </div>
      </div>
    </PlateElement>
  );
}
